import { PLAYER_RADIUS_M } from '../sim/positioning.js';
import { ASSET_SOURCE_PIXELS_PER_METRE } from './asset-source-scale.js';

/**
 * Size contract for future entity sprites. Art is authored at
 * `ASSET_SOURCE_PIXELS_PER_METRE`, centered on the pivot, and facing up
 * (−Y) in the raster; the renderer rotates +π/2 onto the +X facing. The circle is
 * the shape the canvas renderer draws today. Presentation only: these values
 * never feed collision, hit or movement code.
 */
/** Room around the circle for silhouettes that overhang it (weapons, tails, shadows). */
export const ENTITY_SPRITE_MARGIN_FRACTION = 0.25;
/** Every monster is currently drawn at this radius; snapshots carry no type or size. */
export const MONSTER_VISUAL_RADIUS_M = 0.55;

export type EntitySpriteKey = 'PLAYER' | 'MONSTER' | 'PROJECTILE';

export interface EntitySpriteSpec {
  readonly key: EntitySpriteKey;
  /** FIXED draws at `radiusM`; SCALE_TO_RADIUS stretches so the circle matches each object's `radiusM`. */
  readonly sizeMode: 'FIXED' | 'SCALE_TO_RADIUS';
  readonly radiusM: number;
  /** Direction the renderer can rotate the art toward, or NONE for rotation-free art. */
  readonly facing: 'PLAYER_FACING' | 'NONE' | 'VELOCITY';
  readonly radiusSource: string;
  readonly canvasPx: number;
  readonly pivotPx: readonly [number, number];
}

const spec = (key: EntitySpriteKey, sizeMode: EntitySpriteSpec['sizeMode'], radiusM: number,
  facing: EntitySpriteSpec['facing'], radiusSource: string): EntitySpriteSpec => {
  const canvasPx = Math.ceil(2 * radiusM * (1 + ENTITY_SPRITE_MARGIN_FRACTION) * ASSET_SOURCE_PIXELS_PER_METRE);
  return Object.freeze<EntitySpriteSpec>({ key, sizeMode, radiusM, facing, radiusSource, canvasPx, pivotPx: [canvasPx / 2, canvasPx / 2] });
};

export const ENTITY_SPRITE_SPECS_V1: Readonly<Record<EntitySpriteKey, EntitySpriteSpec>> = Object.freeze({
  PLAYER: spec('PLAYER', 'FIXED', PLAYER_RADIUS_M, 'PLAYER_FACING', 'PLAYER_RADIUS_M (collision radius)'),
  MONSTER: spec('MONSTER', 'FIXED', MONSTER_VISUAL_RADIUS_M, 'NONE', 'MONSTER_VISUAL_RADIUS_M (renderer-only)'),
  // Projectile radius varies per skill; the template is a 0.5 m reference stretched at runtime.
  PROJECTILE: spec('PROJECTILE', 'SCALE_TO_RADIUS', 0.5, 'VELOCITY', 'AttackSnapshot.radiusM (per object)'),
});

const round = (value: number): string => String(Math.round(value * 100) / 100);

/** Artist guide: circle outline, pivot cross and facing arrow on a transparent canvas. */
export function entitySpriteTemplateSvg(entity: EntitySpriteSpec): string {
  const size = entity.canvasPx; const [cx, cy] = entity.pivotPx;
  const r = entity.radiusM * ASSET_SOURCE_PIXELS_PER_METRE;
  const cross = size * 0.04;
  const label = `${entity.key} ${entity.sizeMode} r=${entity.radiusM}m (${round(r)}px) canvas=${size}px @${ASSET_SOURCE_PIXELS_PER_METRE}px/m`;
  const arrow = entity.facing === 'NONE' ? '' : [
    `  <path d="M${round(cx)} ${round(cy - r * 0.35)} V${round(cy - r * 0.95)}" stroke="#e03131" stroke-width="${round(size * 0.008)}" fill="none"/>`,
    `  <path d="M${round(cx - r * 0.12)} ${round(cy - r * 0.8)} L${round(cx)} ${round(cy - r * 0.95)} L${round(cx + r * 0.12)} ${round(cy - r * 0.8)}" stroke="#e03131" stroke-width="${round(size * 0.008)}" fill="none"/>`,
  ].join('\n') + '\n';
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
  <title>${label}</title>
  <rect x="0.5" y="0.5" width="${size - 1}" height="${size - 1}" fill="none" stroke="#adb5bd" stroke-dasharray="8 6"/>
  <circle cx="${round(cx)}" cy="${round(cy)}" r="${round(r)}" fill="none" stroke="#1c7ed6" stroke-width="${round(size * 0.006)}"/>
${arrow}  <path d="M${round(cx - cross)} ${round(cy)} H${round(cx + cross)} M${round(cx)} ${round(cy - cross)} V${round(cy + cross)}" stroke="#212529" stroke-width="${round(size * 0.004)}"/>
  <text x="${round(size / 2)}" y="${round(size - size * 0.03)}" font-family="sans-serif" font-size="${round(size * 0.03)}" text-anchor="middle" fill="#495057">${label}</text>
</svg>
`;
}
